import { useLazyGetEmailForBugReportQuery, useLazyGetUISettingsQuery } from 'store/services/core/settings';
import type { ColorType, DashboardItem } from 'types';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { COLORS } from './constants';
import { useLogin } from '..';

type UIContextType = {
  setColor: (colorType: ColorType, colorString: string) => void;
  getColor: (colorType: ColorType) => string;
  dashboard: DashboardItem[] | null;
  email: string | null;
};

const UIContext = createContext<UIContextType>({
  setColor: () => {},
  getColor: () => '',
  dashboard: null,
  email: null,
});

interface UIProviderProps {
  children: React.ReactNode;
}

const UIProvider = ({ children }: UIProviderProps) => {
  const [dashboard, setDashboard] = useState<DashboardItem[] | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  const { user } = useLogin();

  const [triggerFetchForUIData, { data: uiData, isLoading: isUILoading, error: uiError }] = useLazyGetUISettingsQuery();

  const [triggerFetchForEmail, { data: emailData, isLoading: isEmailLoading }] = useLazyGetEmailForBugReportQuery();

  const [colors, setColors] = useState<Record<ColorType, string>>({
    primary: '#2B3674',
    bg_primary: '#F4F7FE',
    primaryLight: '#707EAE',
    button: '#2748FB',
    primaryDark: '#2748FB',
    bg_primaryDark: '#2748FB',
    bg_secondaryDark: '#DEE3FD',
    bg_secondaryLight: '#E6E9F9',
    secondary: '#707EAE',
    bg_secondary: '#707EAE',
    success: '#22B68B',
    error: '#E70707',
  });

  const setColor = useCallback((colorType: ColorType, colorString: string) => {
    document.documentElement.style.setProperty(COLORS[colorType], colorString);
    setColors((prev) => ({ ...prev, [colorType]: colorString }));
  }, []);

  const getColor = useCallback((colorType: ColorType) => colors[colorType], [colors]);

  useEffect(() => {
    if (uiError) return;

    if (user && !isUILoading && !uiData) {
      //triggerFetchForUIData();
    }
    if (user && !isEmailLoading && !emailData) {
      //triggerFetchForEmail();
    }
    if (emailData) {
      setEmail(emailData.email);
    }
    if (uiData) {
      const colours = uiData.uiSettings.reduce(
        (acc, { key, value }) => {
          acc[key as ColorType] = value;
          return acc;
        },
        {} as Record<ColorType, string>
      );
      Object.entries(colours).forEach(([key, value]) => setColor(key as ColorType, value));

      const dashboardValue = uiData.uiSettings.find((setting) => setting.key === 'dashboard')?.value;

      const panel: DashboardItem[] = Array.isArray(dashboardValue)
        ? dashboardValue
        : dashboardValue
          ? JSON.parse(dashboardValue)
          : [{ type: 'info' }, { type: 'activities' }, { type: 'modules' }];

      if (panel) {
        setDashboard(panel);
      }
    }
  }, [user, isUILoading, uiData, uiError, isEmailLoading, emailData, triggerFetchForUIData, triggerFetchForEmail, setColor]);

  const value = useMemo(() => ({ setColor, getColor, dashboard, email }), [dashboard, email, getColor, setColor]);

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export { UIProvider, UIContext };
